import type { InfiniteData } from "@tanstack/react-query";
import type { AgentAttachment, AgentMessage } from "@/lib/types";

/** One page of an agent's transcript, as `GET /agents/:id/messages` returns it. */
export interface TranscriptPage {
  messages: AgentMessage[];
  /**
   * Whether anything is older than this page, decided when it was fetched (a full
   * page). Not re-derived from its length: live messages join the newest page, and
   * a short first page that grows past a page's worth is still the whole story.
   */
  more: boolean;
}

/** `before` for the page after this one: null for the newest page. */
export type TranscriptCursor = string | null;
export type TranscriptData = InfiniteData<TranscriptPage, TranscriptCursor>;

/** How many messages a page asks for; a shorter page means the start was reached. */
export const TRANSCRIPT_PAGE = 200;

const byTime = (a: AgentMessage, b: AgentMessage) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);

/** Where the next (older) page starts: before this page's oldest message, or nowhere. */
export function olderCursor(page: TranscriptPage): string | undefined {
  if (!page.more) return undefined;
  let oldest: AgentMessage | undefined;
  for (const message of page.messages) if (!oldest || byTime(message, oldest) < 0) oldest = message;
  return oldest?.id;
}

/** Every loaded page as one conversation, oldest first, each message once. */
export function flattenTranscript(data: { pages: TranscriptPage[] } | undefined): AgentMessage[] {
  if (!data) return [];
  const byId = new Map<string, AgentMessage>();
  for (const page of data.pages) for (const message of page.messages) byId.set(message.id, message);
  return [...byId.values()].sort(byTime);
}

/**
 * The transcript with one attachment replaced (made public or private), wherever
 * its message sits. The same object back when it is not loaded here.
 */
export function patchTranscriptAttachment(data: TranscriptData | undefined, attachment: AgentAttachment): TranscriptData | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((page) => {
    if (!page.messages.some((message) => message.id === attachment.messageId)) return page;
    return {
      ...page,
      messages: page.messages.map((message) => {
        if (message.id !== attachment.messageId || !message.attachments?.some((file) => file.id === attachment.id)) return message;
        changed = true;
        return { ...message, attachments: message.attachments.map((file) => (file.id === attachment.id ? { ...file, ...attachment } : file)) };
      }),
    };
  });
  return changed ? { ...data, pages } : data;
}

/**
 * The transcript with `incoming` written in: a message already on a page (a tool
 * call getting its result) is replaced where it is, and a new one joins the
 * newest page. No cache yet means nothing to write into — the first fetch will
 * bring it. Returns the same object when nothing changed, so observers do not
 * re-render for a repeat.
 */
export function upsertTranscript(data: TranscriptData | undefined, incoming: AgentMessage[]): TranscriptData | undefined {
  if (!data || data.pages.length === 0 || incoming.length === 0) return data;
  const pages = data.pages.map((page) => ({ ...page }));
  let changed = false;
  for (const message of incoming) {
    let placed = false;
    for (const page of pages) {
      const index = page.messages.findIndex((existing) => existing.id === message.id);
      if (index === -1) continue;
      placed = true;
      if (page.messages[index] !== message) {
        page.messages = page.messages.map((existing, at) => (at === index ? message : existing));
        changed = true;
      }
      break;
    }
    if (!placed) {
      pages[0]!.messages = [...pages[0]!.messages, message].sort(byTime);
      changed = true;
    }
  }
  return changed ? { ...data, pages } : data;
}
